<template>
  <container :isModal="false" :loading="!stateReady">
    <template v-slot:header>
      <h3 v-text="titulo" />
    </template>
    <template v-slot:body>
      <form @submit.prevent="salvar" v-if="stateReady">
        <fieldset :disabled="stateLoading">
          <!-- root: o cliente vem primeiro e escopa transportador, veículos e preços -->
          <template v-if="isRoot">
            <entity-selector-cliente
              :cliente="cliente ?? undefined"
              :idClienteFiltered="cliente?.idCliente ?? undefined"
              :canEdit="!idRntrcSolicitacao && !origem"
              :required="true"
              :focus="!cliente"
              :parentLoading="stateLoading"
              label="Cliente da solicitação"
              @confirm-selection="confirmSelectionCliente"
            />
            <div class="rntrc-aviso rntrc-aviso--info p-mb-3" v-if="!cliente">
              <i class="fas fa-users p-mr-2" />
              Escolha o cliente para ver os transportadores, os veículos e os
              preços dele.
            </div>
          </template>
          <div class="p-formgrid p-grid">
            <div class="p-field p-fluid p-col-12 p-md-6">
              <input-with-label label="Serviço" v-slot="l" :required="true">
                <Dropdown
                  :id="l.id"
                  v-model="tipo"
                  :options="tiposOpcoes"
                  optionValue="flag"
                  optionLabel="descricao"
                  :disabled="!!idRntrcSolicitacao"
                  :placeholder="'&nbsp;'"
                  @change="aoTrocarTipo()"
                />
              </input-with-label>
            </div>
            <div class="p-col-12 p-md-6 rntrc-precos" v-if="tipo">
              <small class="rntrc-rotulo">Preço GovPass</small>
              <div v-if="precosTexto.length">
                <div v-for="p in precosTexto" :key="p" v-text="p" />
              </div>
              <div v-else>---</div>
            </div>
          </div>

          <div
            v-if="origem && ehNovoCadastro"
            class="rntrc-aviso rntrc-aviso--info p-mb-3"
          >
            <i class="fas fa-user-plus p-mr-2" />
            Cadastro na ANTT a partir da consulta nº
            {{ origem.idRntrcSolicitacao }}, que não encontrou o transportador.
            Os dados abaixo vieram do cadastro da ANTT ou da GovPass: confira e
            complete os dados da CNH.
          </div>
          <div
            v-else-if="origem && origem.flTipoServico === 'CVE'"
            class="rntrc-aviso rntrc-aviso--info p-mb-3"
          >
            <i class="fas fa-search p-mr-2" />
            Placas da consulta nº {{ origem.idRntrcSolicitacao }}: renavam e
            tipo vieram do extrato da ANTT, confira.
            <span v-if="exigeServicoVeiculo">
              Cadastre cada veículo com o CRLV no botão
              <i class="pi pi-plus-circle" /> ao lado da placa.</span
            >
            <span v-else-if="noNomeDoDonoAtual">
              O CPF/CNPJ é de quem tem a placa hoje.</span
            >
          </div>
          <div v-else-if="origem" class="rntrc-aviso rntrc-aviso--info p-mb-3">
            <i class="fas fa-exchange-alt p-mr-2" />
            Inclusão no destino das placas excluídas na solicitação nº
            {{ origem.idRntrcSolicitacao }}. Informe o transportador de destino
            e confira o renavam de cada placa.
          </div>

          <!-- Transportador / CPF-CNPJ (pessoa do cadastro da ANTT) -->
          <template v-if="tipo && exigeCpfCnpj && clienteOk">
            <h4 class="p-mt-2" v-text="labelTransportador" />
            <div
              v-if="tipo === 'IMF' && proprietariosDivergentes"
              class="rntrc-aviso rntrc-aviso--warning p-mb-2"
            >
              <i class="fas fa-exclamation-triangle p-mr-2" />
              As placas escolhidas têm proprietários diferentes:
              <b>{{ transportadorDosVeiculos }}</b
              >. Na inclusão própria todas precisam ser do mesmo proprietário;
              remova as de outro dono ou atualize o cadastro do veículo.
            </div>
            <div
              v-else-if="tipo === 'IMF'"
              class="rntrc-aviso rntrc-aviso--muted p-mb-2"
            >
              <i class="fas fa-user-check p-mr-2" />
              Na inclusão própria o transportador é o proprietário dos veículos
              escolhidos abaixo:
              <b v-if="transportadorDosVeiculos">{{
                transportadorDosVeiculos
              }}</b>
              <span v-else>escolha ou cadastre os veículos.</span>
            </div>
            <div v-else class="p-formgrid p-grid p-ai-end">
              <div class="p-field p-fluid p-col-12 p-md-6 p-lg-3">
                <div class="p-inputgroup">
                  <input-with-label
                    label="CPF/CNPJ"
                    v-slot="l"
                    :required="true"
                  >
                    <InputText
                      :id="l.id"
                      v-model="cpfCnpj"
                      :required="l.required"
                      :class="{ 'p-invalid': cpfCnpjInvalido }"
                      v-maska="['###.###.###-##', '##.###.###/####-##']"
                      maxlength="18"
                      @blur="identificarPessoa()"
                    />
                  </input-with-label>
                  <Button
                    type="button"
                    icon="pi pi-search"
                    class="p-button-icon-only"
                    style="min-width: initial"
                    v-title="
                      'Buscar ' + papelPessoa.toLowerCase() + ' já cadastrado'
                    "
                    @click="abrirBuscaPessoa()"
                  />
                </div>
                <small class="p-error" v-if="cpfCnpjInvalido"
                  >CPF/CNPJ inválido</small
                >
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6 p-lg-4">
                <input-with-label
                  label="Nome ou razão social"
                  v-slot="l"
                  :required="exigeNomePessoa"
                >
                  <InputText
                    :id="l.id"
                    v-model="nomeTransportador"
                    :required="l.required"
                    :disabled="!!pessoa?.idPessoaAntt"
                    maxlength="250"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-col-12 p-lg-5">
                <button-group>
                  <Button
                    type="button"
                    icon="pi pi-user-plus"
                    :label="'Novo ' + papelPessoa.toLowerCase()"
                    class="p-button-rounded"
                    v-if="!pessoa?.idPessoaAntt"
                    @click="abrirCadastroPessoa(false)"
                  />
                  <Button
                    type="button"
                    icon="pi pi-pencil"
                    label="Alterar cadastro"
                    class="p-button-rounded"
                    v-else
                    @click="abrirCadastroPessoa(true)"
                  />
                  <Button
                    type="button"
                    icon="pi pi-times"
                    label="Trocar"
                    class="p-button-rounded p-button-text"
                    v-if="pessoa?.idPessoaAntt"
                    @click="limparPessoa()"
                  />
                </button-group>
              </div>
              <div class="p-col-12 p-pt-0" v-if="situacaoPessoa">
                <span
                  class="aet-badge"
                  :class="'aet-badge--' + situacaoPessoa.cor"
                  v-text="situacaoPessoa.texto"
                />
              </div>
              <div
                class="p-field p-fluid p-col-12 p-md-6 p-lg-3"
                v-if="tipo === 'TRF'"
              >
                <input-with-label
                  label="CPF/CNPJ de destino"
                  v-slot="l"
                  :required="true"
                >
                  <InputText
                    :id="l.id"
                    v-model="cpfCnpjDestino"
                    :required="l.required"
                    v-maska="['###.###.###-##', '##.###.###/####-##']"
                    maxlength="18"
                  />
                </input-with-label>
              </div>
            </div>
          </template>

          <!-- Novo cadastro PF/PJ -->
          <template v-if="tipo === 'CPF' || tipo === 'CPJ'">
            <h4 class="p-mt-2">Dados do cadastro</h4>
            <div class="p-formgrid p-grid">
              <template v-if="tipo === 'CPF'">
                <div class="p-field p-fluid p-col-12 p-md-4">
                  <input-with-label label="RG" v-slot="l" :required="true">
                    <InputText
                      :id="l.id"
                      v-model="cadastro.rg"
                      required
                      maxlength="20"
                    />
                  </input-with-label>
                </div>
                <div class="p-field p-fluid p-col-12 p-md-4">
                  <input-with-label
                    label="Órgão emissor"
                    v-slot="l"
                    :required="true"
                  >
                    <InputText
                      :id="l.id"
                      v-model="cadastro.orgaoEmissorIdentidade"
                      required
                      maxlength="10"
                    />
                  </input-with-label>
                </div>
                <div class="p-field p-fluid p-col-12 p-md-4">
                  <input-with-label
                    label="UF da identidade"
                    v-slot="l"
                    :required="true"
                  >
                    <InputText
                      :id="l.id"
                      v-model="cadastro.ufIdentidade"
                      required
                      maxlength="2"
                    />
                  </input-with-label>
                </div>
              </template>
              <div class="p-field p-fluid p-col-12 p-md-3">
                <input-with-label
                  label="Tipo de endereço"
                  v-slot="l"
                  :required="true"
                >
                  <InputText
                    :id="l.id"
                    v-model="cadastro.enderecoTipo"
                    required
                    maxlength="20"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-3">
                <input-with-label label="CEP" v-slot="l" :required="true">
                  <InputText
                    :id="l.id"
                    v-model="cadastro.enderecoCep"
                    required
                    v-maska="'#####-###'"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6">
                <input-with-label
                  label="Logradouro"
                  v-slot="l"
                  :required="true"
                >
                  <InputText
                    :id="l.id"
                    v-model="cadastro.enderecoLogradouro"
                    required
                    maxlength="100"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-3">
                <input-with-label label="Número" v-slot="l" :required="true">
                  <InputText
                    :id="l.id"
                    v-model="cadastro.enderecoNumero"
                    required
                    maxlength="20"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-3">
                <input-with-label label="Bairro" v-slot="l" :required="true">
                  <InputText
                    :id="l.id"
                    v-model="cadastro.enderecoBairro"
                    required
                    maxlength="60"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-3">
                <input-with-label label="Telefone" v-slot="l" :required="true">
                  <InputText
                    :id="l.id"
                    v-model="cadastro.contatoTelefone"
                    required
                    maxlength="20"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-3">
                <input-with-label label="E-mail" v-slot="l" :required="true">
                  <InputText
                    :id="l.id"
                    type="email"
                    v-model="cadastro.contatoEmail"
                    required
                    maxlength="100"
                  />
                </input-with-label>
              </div>
            </div>

            <!-- CNH: gestor (sócio proprietário) no ETC, motorista (o próprio TAC) no TAC -->
            <h4 class="p-mt-2" v-text="tituloCnh" />
            <div class="p-formgrid p-grid">
              <template v-if="tipo === 'CPJ'">
                <div class="p-field p-fluid p-col-12 p-md-3">
                  <input-with-label label="CPF" v-slot="l" :required="true">
                    <InputText
                      :id="l.id"
                      v-model="cadastro.cnhCpf"
                      required
                      v-maska="'###.###.###-##'"
                    />
                  </input-with-label>
                </div>
                <div class="p-field p-fluid p-col-12 p-md-6">
                  <input-with-label label="Nome" v-slot="l" :required="true">
                    <InputText
                      :id="l.id"
                      v-model="cadastro.cnhNome"
                      required
                      maxlength="250"
                    />
                  </input-with-label>
                </div>
              </template>
              <template v-else>
                <div class="p-field p-fluid p-col-12 p-md-3">
                  <input-with-label label="Nº da CNH" v-slot="l">
                    <InputText
                      :id="l.id"
                      v-model="cadastro.cnhNumero"
                      maxlength="20"
                    />
                  </input-with-label>
                </div>
                <div class="p-field p-fluid p-col-12 p-md-2">
                  <input-with-label
                    label="Categoria"
                    v-slot="l"
                    :required="true"
                  >
                    <Dropdown
                      :id="l.id"
                      v-model="cadastro.cnhCategoria"
                      :options="categoriasCnh"
                      :placeholder="'&nbsp;'"
                    />
                  </input-with-label>
                </div>
                <div class="p-field p-fluid p-col-12 p-md-2">
                  <input-with-label label="Sexo" v-slot="l" :required="true">
                    <Dropdown
                      :id="l.id"
                      v-model="cadastro.cnhSexo"
                      :options="sexosCnh"
                      optionValue="flag"
                      optionLabel="descricao"
                      :placeholder="'&nbsp;'"
                    />
                  </input-with-label>
                </div>
              </template>
              <div class="p-field p-fluid p-col-12 p-md-3">
                <input-with-label
                  label="Nascimento (dd/mm/aaaa)"
                  v-slot="l"
                  :required="true"
                >
                  <InputText
                    :id="l.id"
                    v-model="cadastro.cnhDataNascimento"
                    required
                    v-maska="'##/##/####'"
                  />
                </input-with-label>
              </div>
            </div>
          </template>

          <!-- Veículos (cadastro da ANTT) -->
          <template v-if="tipo && comVeiculos && clienteOk">
            <h4 class="p-mt-2">
              {{ tipo === "CVE" ? "Placas a consultar" : "Veículos" }}
            </h4>
            <!-- Consulta de veículo: só a placa, sem cadastro do veículo. -->
            <div class="p-formgrid p-grid p-ai-end" v-if="tipo === 'CVE'">
              <div class="p-field p-fluid p-col-12 p-md-8 p-lg-6">
                <input-with-label label="Placas" v-slot="l">
                  <Textarea
                    :id="l.id"
                    v-model="busca.placa"
                    rows="2"
                    :autoResize="true"
                    @keydown.enter.prevent="adicionarPlacas()"
                  />
                </input-with-label>
                <small>
                  Uma ou várias placas, separadas por espaço, vírgula ou linha
                  (dá para colar uma coluna da planilha).
                </small>
              </div>
              <div class="p-field p-col-12 p-md-4 p-lg-6">
                <Button
                  type="button"
                  icon="pi pi-plus"
                  label="Adicionar placas"
                  class="p-button-rounded"
                  :disabled="!busca.placa"
                  @click="adicionarPlacas()"
                />
              </div>
            </div>
            <div class="p-formgrid p-grid p-ai-end" v-else>
              <div class="p-field p-fluid p-col-12 p-md-4 p-lg-3">
                <input-with-label label="Placa" v-slot="l">
                  <InputText
                    :id="l.id"
                    v-model="busca.placa"
                    maxlength="8"
                    @keydown.enter.prevent="abrirBuscaVeiculos()"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-col-12 p-md-8 p-lg-9">
                <button-group>
                  <Button
                    type="button"
                    icon="pi pi-search"
                    label="Buscar no cadastro"
                    class="p-button-rounded p-button-outlined"
                    @click="abrirBuscaVeiculos()"
                  />
                  <Button
                    type="button"
                    icon="pi pi-plus-circle"
                    label="Novo veículo"
                    class="p-button-rounded"
                    @click="abrirCadastroVeiculo(null, busca.placa, -1)"
                  />
                  <Button
                    v-if="permiteVeiculoDigitado"
                    type="button"
                    icon="pi pi-plus"
                    label="Adicionar placa digitada"
                    class="p-button-rounded p-button-text"
                    @click="adicionarDigitado()"
                  />
                </button-group>
              </div>
            </div>
            <DataTable
              :value="veiculos"
              class="p-datatable-sm"
              responsiveLayout="stack"
              breakpoint="60rem"
              :stripedRows="true"
            >
              <template #empty>Nenhum veículo adicionado.</template>
              <Column header="Placa" style="width: 11rem">
                <template #body="{ data }">
                  <b
                    v-if="somenteLeitura(data)"
                    v-text="formatPlaca(data.placa)"
                  />
                  <InputText
                    v-else
                    v-model="data.placa"
                    class="p-inputtext-sm rntrc-input-celula"
                    :class="{
                      'p-invalid': data.placa && !isPlacaValida(data.placa)
                    }"
                    maxlength="8"
                    @blur="data.placa = normalizarPlaca(data.placa)"
                  />
                </template>
              </Column>
              <Column
                header="Renavam"
                v-if="tipo !== 'CVE'"
                style="width: 11rem"
              >
                <template #body="{ data }">
                  <span
                    v-if="somenteLeitura(data)"
                    v-text="data.renavam || '---'"
                  />
                  <InputText
                    v-else
                    v-model="data.renavam"
                    class="p-inputtext-sm rntrc-input-celula"
                    :class="{
                      'p-invalid':
                        data.renavam && !isRenavamValido(data.renavam)
                    }"
                    v-maska="'###########'"
                    placeholder="opcional"
                  />
                </template>
              </Column>
              <Column header="Tipo" v-if="exigeServicoVeiculo">
                <template #body="{ data }">
                  <span
                    v-text="
                      getServicoVeiculoLabel(data.flServicoVeiculo) || '---'
                    "
                  />
                </template>
              </Column>
              <Column :header="tituloProprietario" v-if="exigeServicoVeiculo">
                <template #body="{ data }">
                  <span
                    v-if="data.proprietario?.cpfCnpj"
                    v-text="getNomePessoaAntt(data.proprietario)"
                  />
                  <span v-else>---</span>
                </template>
              </Column>
              <Column header="CRLV" v-if="exigeServicoVeiculo">
                <template #body="{ data }">
                  <span
                    v-if="data.doCadastro"
                    class="aet-badge"
                    :class="
                      data.possuiCrlv
                        ? 'aet-badge--success'
                        : 'aet-badge--danger'
                    "
                    v-text="data.possuiCrlv ? 'Sim' : 'Não'"
                  />
                  <span v-else class="aet-badge aet-badge--warning"
                    >Cadastre o veículo</span
                  >
                </template>
              </Column>
              <Column
                header="Contrato de arrendamento"
                v-if="tipo === 'IAR'"
                style="min-width: 12rem"
              >
                <template #body="{ data }">
                  <!-- o campo nativo fica escondido: o envio lê o arquivo dele -->
                  <div class="rntrc-contrato">
                    <input
                      type="file"
                      class="rntrc-arquivo-oculto"
                      :id="idArquivoContrato(data)"
                      accept="application/pdf,image/*"
                      @change="aoEscolherContrato(data, $event)"
                    />
                    <Button
                      type="button"
                      icon="pi pi-paperclip"
                      label="Escolher arquivo"
                      class="p-button-outlined p-button-sm rntrc-botao-arquivo"
                      v-title="
                        data.possuiContrato
                          ? 'Contrato já enviado: escolha outro para substituir'
                          : 'Opcional: sem contrato, o CRLV vai no lugar'
                      "
                      @click="escolherContrato(data)"
                    />
                    <small
                      v-if="data.nomeContrato"
                      class="rntrc-contrato-dica"
                      v-text="data.nomeContrato"
                    />
                    <small
                      v-else-if="data.possuiContrato"
                      class="rntrc-contrato-dica"
                      >Enviado</small
                    >
                  </div>
                </template>
              </Column>
              <Column header="Valor GovPass" v-if="pago">
                <template #body="{ data }">
                  {{ formatMoeda(precoVeiculo(data)) }}
                </template>
              </Column>
              <Column style="width: 9rem; text-align: right">
                <template #body="{ data, index }">
                  <button-group>
                    <Button
                      v-if="data.idVeiculoAntt"
                      type="button"
                      icon="pi pi-pencil"
                      class="p-button-rounded p-button-text"
                      v-title="'Alterar o veículo (CRLV, proprietário...)'"
                      @click="
                        abrirCadastroVeiculo(data.idVeiculoAntt, null, index)
                      "
                    />
                    <Button
                      v-else-if="exigeServicoVeiculo"
                      type="button"
                      icon="pi pi-plus-circle"
                      class="p-button-rounded p-button-text"
                      v-title="'Cadastrar o veículo (proprietário e CRLV)'"
                      @click="abrirCadastroVeiculo(null, data.placa, index)"
                    />
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      class="p-button-rounded p-button-danger p-button-text"
                      v-title="'Remover'"
                      @click="removerVeiculo(index)"
                    />
                  </button-group>
                </template>
              </Column>
            </DataTable>
            <div
              class="p-d-flex p-jc-end p-mt-2"
              v-if="pago && veiculos.length"
            >
              <b>Total estimado GovPass: {{ formatMoeda(totalEstimado) }}</b>
            </div>

            <div
              class="p-field-checkbox p-mt-3"
              v-if="['IMF', 'IAR', 'EXC', 'ALT', 'TRF'].indexOf(tipo) >= 0"
            >
              <Checkbox
                id="rntrc-prosseguir-parcial"
                v-model="flProsseguirParcial"
                :binary="true"
              />
              <label for="rntrc-prosseguir-parcial">
                Prosseguir com as demais placas se alguma for recusada pela ANTT
              </label>
            </div>
          </template>

          <div class="p-formgrid p-grid p-mt-2" v-if="tipo">
            <div class="p-field p-fluid p-col-12">
              <input-with-label label="Observação" v-slot="l">
                <Textarea
                  :id="l.id"
                  v-model="observacao"
                  :autoResize="true"
                  rows="2"
                  maxlength="1000"
                />
              </input-with-label>
            </div>
          </div>

          <!-- Avisos obrigatórios -->
          <div class="rntrc-aviso rntrc-aviso--warning p-mt-2" v-if="tipo">
            <h4 class="p-mt-0">Avisos</h4>
            <ul class="p-m-0 p-pl-4">
              <li v-for="a in avisos" :key="a" v-text="a" />
            </ul>
            <div class="p-field-checkbox p-mt-3 p-mb-0">
              <Checkbox
                id="rntrc-ciente"
                v-model="cienteAvisos"
                :binary="true"
              />
              <label for="rntrc-ciente"><b>Li e concordo</b></label>
            </div>
          </div>

          <div
            class="rntrc-aviso rntrc-aviso--danger p-mt-3"
            v-if="erros.length"
          >
            <ul class="p-m-0 p-pl-4">
              <li v-for="e in erros" :key="e" v-text="e" />
            </ul>
          </div>

          <div class="p-mt-3">
            <button-group>
              <Button
                type="submit"
                class="p-button-rounded"
                :label="
                  idRntrcSolicitacao
                    ? 'Salvar e reenviar'
                    : 'Enviar solicitação'
                "
                :disabled="!tipo || !cienteAvisos"
                :loading="stateLoading"
              />
              <Button
                type="button"
                label="Voltar"
                class="p-button-rounded p-button-text"
                @click="voltar()"
              />
            </button-group>
          </div>
        </fieldset>
      </form>
      <busca-veiculo-antt
        v-if="busca.aberta"
        :resultados="busca.resultados"
        :carregando="busca.carregando"
        :placaInicial="busca.placa"
        :blockedSelection="motivoBloqueioCadastro"
        :outroProprietario="proprietarioDivergente"
        @buscar="buscarVeiculos"
        @confirm-selection="aoConfirmarVeiculos"
        @cancel-selection="busca.aberta = false"
        @abrir-cadastro="(id, placa) => abrirCadastroVeiculo(id, placa, -1)"
      />
      <busca-pessoa-antt
        v-if="buscaPessoaAberta"
        :cliente="cliente ?? undefined"
        :papel="papelPessoa"
        :termoInicial="termoBuscaPessoa"
        @confirm-selection="aoEscolherPessoa"
        @cancel-selection="buscaPessoaAberta = false"
      />
      <cadastro-pessoa-antt
        v-if="dialogPessoa.aberto"
        :idPessoaAntt="dialogPessoa.idPessoaAntt ?? undefined"
        :cliente="cliente ?? undefined"
        :papel="papelPessoa"
        :cpfCnpjInicial="dialogPessoa.cpfCnpjInicial"
        :nomeInicial="dialogPessoa.nomeInicial"
        @after-register-screen-closed="aoFecharCadastroPessoa"
      />
      <cadastro-veiculo-antt
        v-if="dialogVeiculo.aberto"
        :idVeiculoAntt="dialogVeiculo.idVeiculoAntt ?? undefined"
        :cliente="cliente ?? undefined"
        :placaInicial="dialogVeiculo.placaInicial"
        :renavamInicial="dialogVeiculo.renavamInicial ?? undefined"
        :flServicoVeiculoInicial="
          dialogVeiculo.flServicoVeiculoInicial ?? undefined
        "
        @after-register-screen-closed="aoFecharCadastroVeiculo"
      />
    </template>
  </container>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  PropType,
  Ref
} from "vue";
import { useRouter } from "vue-router";
import Checkbox from "primevue/checkbox";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import BuscaPessoaAntt from "./pessoa/BuscaPessoaAntt.vue";
import BuscaVeiculoAntt from "./veiculo/BuscaVeiculoAntt.vue";
import CadastroPessoaAntt from "./pessoa/CadastroPessoaAntt.vue";
import CadastroVeiculoAntt from "./veiculo/CadastroVeiculoAntt.vue";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import { useAuth } from "../../modules/auth";
import { useConfirm, useToast } from "../../modules/message";
import { useStorage } from "../../modules/storage";
import {
  useRntrcSolicitacaoService,
  useRntrcTipos,
  servicoNoNomeDoDonoAtual
} from "../../modules/business/rntrc/rntrcSolicitacaoBusiness";
import {
  PessoaAnttPayload,
  getNomePessoaAntt,
  usePessoaAnttService
} from "../../modules/business/rntrc/pessoa/pessoaAnttBusiness";
import {
  VeiculoAnttPayload,
  useVeiculoAnttService
} from "../../modules/business/rntrc/veiculo/veiculoAnttBusiness";
import {
  FlagOption,
  RntrcSolicitacaoPayload,
  RntrcSolicitacaoVeiculoPayload,
  RntrcTipoServicoPayload,
  SERVICOS_VEICULO_RNTRC,
  ServicoVeiculoRntrc,
  TipoServicoRntrc,
  formatCpfCnpj,
  formatMoeda,
  formatPlaca,
  getServicoVeiculoLabel,
  getTipoServicoLabel,
  isCpfCnpjValido,
  isPlacaValida,
  isRenavamValido,
  normalizarPlaca,
  somenteDigitos,
  tipoComVeiculos,
  tipoPago
} from "../../modules/business/rntrc/rntrcTypes";

interface VeiculoForm extends RntrcSolicitacaoVeiculoPayload {
  /** Veículo do cadastro da ANTT (placa, renavam e tipo vêm de lá). */
  doCadastro: boolean;
  /** Quem consta no CRLV: o transportador na IMF, o arrendante na IAR. */
  proprietario?: PessoaAnttPayload | null;
  /** Identifica o campo de arquivo do contrato da linha. */
  uid: string;
  /** Placa vinda da consulta de veículo: placa e renavam são os do extrato da ANTT. */
  daConsulta?: boolean;
  /** IAR: nome do arquivo de contrato escolhido (o arquivo fica no campo da linha). */
  nomeContrato?: string | null;
}

interface CadastroNovoCadastro {
  rg?: string | null;
  orgaoEmissorIdentidade?: string | null;
  ufIdentidade?: string | null;
  enderecoTipo?: string | null;
  enderecoCep?: string | null;
  enderecoLogradouro?: string | null;
  enderecoNumero?: string | null;
  enderecoBairro?: string | null;
  contatoTelefone?: string | null;
  contatoEmail?: string | null;
  // titular da CNH: gestor (sócio) no ETC, motorista (o próprio TAC) no TAC
  cnhCpf?: string | null;
  cnhNome?: string | null;
  /** dd/mm/aaaa no formulário; vai como yyyy-mm-dd para a API */
  cnhDataNascimento?: string | null;
  cnhNumero?: string | null;
  cnhCategoria?: string | null;
  cnhSexo?: string | null;
}

interface DialogPessoa {
  aberto: boolean;
  idPessoaAntt: number | null;
  cpfCnpjInicial: string;
  nomeInicial: string;
}

interface DialogVeiculo {
  aberto: boolean;
  idVeiculoAntt: number | null;
  placaInicial: string;
  /** Linha ainda sem cadastro (ex.: vinda da consulta): renavam e tipo para preencher. */
  renavamInicial?: string | null;
  flServicoVeiculoInicial?: ServicoVeiculoRntrc | null;
  /** Linha da tabela de veículos que o cadastro atualiza; -1 = adicionar. */
  indice: number;
}

const CATEGORIAS_CNH = ["A", "B", "C", "D", "E", "AB", "AC", "AD", "AE"];

const SEXOS_CNH = [
  { flag: "M", descricao: "Masculino" },
  { flag: "F", descricao: "Feminino" }
];

/** dd/mm/aaaa → yyyy-mm-dd (ou null se incompleta) */
const dataParaApi = (data?: string | null): string | null => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((data ?? "").trim());
  return m ? m[3] + "-" + m[2] + "-" + m[1] : null;
};

/**
 * yyyy-mm-dd → dd/mm/aaaa. Campos que começam com "data" chegam como Date (o módulo de API
 * converte), os demais como texto.
 */
const dataDaApi = (data?: string | Date | null): string | null => {
  if (data instanceof Date) {
    if (isNaN(data.getTime())) {
      return null;
    }
    // o módulo de API cria a data à meia-noite local (parseISO)
    const dd = ("0" + data.getDate()).slice(-2);
    const mm = ("0" + (data.getMonth() + 1)).slice(-2);
    return dd + "/" + mm + "/" + data.getFullYear();
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(data ?? "");
  return m ? m[3] + "/" + m[2] + "/" + m[1] : null;
};

const novoUid = () =>
  "v" +
  Math.random()
    .toString(36)
    .substring(2);

/** Mesmo limite de placas por solicitação da API (RntrcSolicitacaoValidator.MAXIMO_VEICULOS). */
const MAXIMO_PLACAS_CONSULTA = 20;

interface CadastroRntrcState {
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
  tipo: TipoServicoRntrc | null;
  /** Pessoa do pedido no cadastro da ANTT (transportador; arrendatário na IAR). */
  pessoa: PessoaAnttPayload | null;
  /** O documento digitado foi achado na GovPass (ainda não está na ANTT). */
  pessoaDaGovpass: boolean;
  /** Último documento consultado (evita repetir a busca). */
  documentoIdentificado: string;
  cpfCnpj: string;
  nomeTransportador: string;
  cpfCnpjDestino: string;
  veiculos: Array<VeiculoForm>;
  flProsseguirParcial: boolean;
  observacao: string;
  cienteAvisos: boolean;
  cadastro: CadastroNovoCadastro;
  origem: RntrcSolicitacaoPayload | null;
  cliente: ClientePayload | null;
  tiposCliente: Array<RntrcTipoServicoPayload> | null;
  erros: Array<string>;
  /** Consulta de veículos (pop-up): placa digitada e resultados ANTT + GovPass. */
  busca: {
    placa: string;
    aberta: boolean;
    carregando: boolean;
    resultados: Array<VeiculoAnttPayload>;
  };
  dialogPessoa: DialogPessoa;
  dialogVeiculo: DialogVeiculo;
  /** Lupa do transportador: busca na base do cliente. */
  buscaPessoaAberta: boolean;
  termoBuscaPessoa: string;
}

/** Tipos que o formulário sabe montar, na ordem do menu. */
const TIPOS_FORMULARIO: Array<TipoServicoRntrc> = [
  "IMF",
  "IAR",
  "EXC",
  "CTR",
  "CVE",
  "RPE",
  "TRF",
  "ALT",
  "CPF",
  "CPJ",
  "REA"
];

/** Consultas aceitam o documento digitado sem cadastrar a pessoa. */
const TIPOS_DOCUMENTO_LIVRE: Array<TipoServicoRntrc> = ["CTR", "RPE"];

export default defineComponent({
  setup(props) {
    const router = useRouter();
    const { loadTipos, loadTiposCliente, tipos } = useRntrcTipos();

    const state = reactive<CadastroRntrcState>({
      stateReady: false,
      loadings: [],
      tipo: null,
      pessoa: null,
      pessoaDaGovpass: false,
      documentoIdentificado: "",
      cpfCnpj: "",
      nomeTransportador: "",
      cpfCnpjDestino: "",
      veiculos: [],
      flProsseguirParcial: false,
      observacao: "",
      cienteAvisos: false,
      cadastro: {},
      origem: null,
      cliente: null,
      tiposCliente: null,
      erros: [],
      busca: { placa: "", aberta: false, carregando: false, resultados: [] },
      dialogPessoa: {
        aberto: false,
        idPessoaAntt: null,
        cpfCnpjInicial: "",
        nomeInicial: ""
      },
      dialogVeiculo: {
        aberto: false,
        idVeiculoAntt: null,
        placaInicial: "",
        indice: -1
      },
      buscaPessoaAberta: false,
      termoBuscaPessoa: ""
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    // ---------------------------------------------------------------------
    // Tipo e regras por tipo
    // ---------------------------------------------------------------------

    const isRoot = computed(
      () => !!useAuth().amIAllowed("view", "root-dashboard")
    );

    /** Não root: sempre ok; root: só depois de escolher o cliente. */
    const clienteOk = computed(() => !isRoot.value || !!state.cliente);

    /** Cliente das buscas e dos cadastros da ANTT: só o root informa. */
    const idClienteBusca = computed(() =>
      isRoot.value ? state.cliente?.idCliente ?? null : null
    );

    /** Root com cliente escolhido: tipos e preços daquele cliente. */
    const tiposAtuais = computed<Array<RntrcTipoServicoPayload>>(() =>
      isRoot.value && state.tiposCliente ? state.tiposCliente : tipos.value
    );

    const tiposOpcoes = computed(() => {
      const habilitados = tiposAtuais.value.map((t) => t.flTipoServico);
      const lista = TIPOS_FORMULARIO.filter(
        (t) => habilitados.indexOf(t) >= 0 || t === state.tipo
      );
      return lista.map(
        (t) =>
          ({ flag: t, descricao: getTipoServicoLabel(t) } as FlagOption<
            TipoServicoRntrc
          >)
      );
    });

    const tipoInfo = computed<RntrcTipoServicoPayload | undefined>(() =>
      tiposAtuais.value.find((t) => t.flTipoServico === state.tipo)
    );

    const titulo = computed(() => {
      if (props.idRntrcSolicitacao) {
        return (
          "Editar solicitação RNTRC nº " +
          props.idRntrcSolicitacao +
          (state.tipo ? " — " + getTipoServicoLabel(state.tipo) : "")
        );
      }
      return state.tipo
        ? "Nova solicitação RNTRC — " + getTipoServicoLabel(state.tipo)
        : "Nova solicitação RNTRC";
    });

    const comVeiculos = computed(() =>
      tipoInfo.value?.comVeiculos !== undefined &&
      tipoInfo.value?.comVeiculos !== null
        ? !!tipoInfo.value.comVeiculos
        : tipoComVeiculos(state.tipo)
    );

    const pago = computed(() =>
      tipoInfo.value?.pago !== undefined && tipoInfo.value?.pago !== null
        ? !!tipoInfo.value.pago
        : tipoPago(state.tipo)
    );

    const exigeCpfCnpj = computed(() =>
      tipoInfo.value?.exigeCpfCnpj !== undefined &&
      tipoInfo.value?.exigeCpfCnpj !== null
        ? !!tipoInfo.value.exigeCpfCnpj
        : state.tipo !== "CVE"
    );

    /** Inclusão: o veículo vem do cadastro da ANTT (renavam, tipo, proprietário, CRLV). */
    const ehInclusao = computed(
      () => state.tipo === "IMF" || state.tipo === "IAR"
    );

    const exigeServicoVeiculo = ehInclusao;

    /** Placa digitada só onde o veículo não precisa de cadastro (exclusão, consulta). */
    const permiteVeiculoDigitado = computed(() => !ehInclusao.value);

    /** Consulta aceita o documento digitado; os demais cadastram a pessoa (pedem o nome). */
    const exigeNomePessoa = computed(
      () =>
        !!state.tipo &&
        TIPOS_DOCUMENTO_LIVRE.indexOf(state.tipo) < 0 &&
        !state.pessoa?.idPessoaAntt
    );

    const papelPessoa = computed(() => {
      switch (state.tipo) {
        case "IAR":
          return "Arrendatário";
        case "EXC":
          return "Transportador atual";
        default:
          return "Transportador";
      }
    });

    const labelTransportador = computed(() => {
      switch (state.tipo) {
        case "EXC":
          return "Quem tem a placa hoje";
        case "IAR":
          return "Arrendatário (quem registra o veículo no seu nome)";
        case "TRF":
          return "Transportador de origem";
        default:
          return "Transportador";
      }
    });

    const tituloProprietario = computed(() =>
      state.tipo === "IAR" ? "Arrendante (proprietário)" : "Proprietário"
    );

    const cpfCnpjInvalido = computed(
      () =>
        somenteDigitos(state.cpfCnpj).length >= 11 &&
        !isCpfCnpjValido(state.cpfCnpj)
    );

    const situacaoPessoa = computed(() => {
      if (state.pessoa?.idPessoaAntt) {
        return { cor: "success", texto: "No cadastro da ANTT" };
      }
      if (state.pessoaDaGovpass) {
        return {
          cor: "info",
          texto: "Dados da GovPass: entra no cadastro da ANTT ao enviar"
        };
      }
      if (
        isCpfCnpjValido(state.cpfCnpj) &&
        state.tipo &&
        TIPOS_DOCUMENTO_LIVRE.indexOf(state.tipo) < 0
      ) {
        return {
          cor: "warning",
          texto: "Novo: entra no cadastro da ANTT ao enviar"
        };
      }
      return null;
    });

    /** IMF: o transportador é o proprietário dos veículos (um só). */
    const transportadorDosVeiculos = computed(() => {
      const nomes = state.veiculos
        .filter((v) => v.proprietario?.cpfCnpj)
        .map((v) => getNomePessoaAntt(v.proprietario))
        .filter((n, i, lista) => lista.indexOf(n) === i);
      return nomes.join(" | ");
    });

    const proprietariosDivergentes = computed(
      () =>
        new Set(
          state.veiculos
            .filter((v) => v.proprietario?.cpfCnpj)
            .map((v) => somenteDigitos(v.proprietario?.cpfCnpj))
        ).size > 1
    );

    /** IMF: o proprietário das placas já escolhidas (fora a da linha indicada). */
    const donoDaInclusao = (indiceIgnorado: number) =>
      state.veiculos.find(
        (r, i) => i !== indiceIgnorado && !!r.proprietario?.cpfCnpj
      )?.proprietario ?? null;

    /**
     * IMF: o dono da inclusão quando a placa é de outro proprietário; senão null. Na inclusão
     * própria o transportador é o proprietário, então todas as placas têm de ser dele.
     */
    const proprietarioDivergente = (
      v: VeiculoAnttPayload,
      indiceIgnorado = -1
    ): PessoaAnttPayload | null => {
      if (state.tipo !== "IMF" || !v.proprietario?.cpfCnpj) {
        return null;
      }
      const dono = donoDaInclusao(indiceIgnorado);
      if (
        !dono ||
        somenteDigitos(dono.cpfCnpj) === somenteDigitos(v.proprietario.cpfCnpj)
      ) {
        return null;
      }
      return dono;
    };

    /** Em nome de quem o pedido (ou a consulta) é aberto na ANTT, para o card de avisos. */
    const avisoProcurador = computed(() => {
      const consulta =
        state.tipo === "CTR" || state.tipo === "CVE" || state.tipo === "RPE";
      const base =
        (consulta ? "A consulta é feita" : "O pedido é feito") +
        " na ANTT pelo procurador da GovPass, com o login da GovPass";
      if (state.tipo === "CVE") {
        return base + ".";
      }
      if (state.tipo === "IMF") {
        return transportadorDosVeiculos.value && !proprietariosDivergentes.value
          ? base +
              ", em nome de " +
              transportadorDosVeiculos.value +
              " (transportador, proprietário dos veículos)."
          : base +
              ", em nome do transportador (proprietário dos veículos escolhidos).";
      }
      const papel = papelPessoa.value.toLowerCase();
      const documento = somenteDigitos(state.cpfCnpj);
      if (!isCpfCnpjValido(documento)) {
        return consulta
          ? base + "."
          : base + ", em nome do " + papel + " informado.";
      }
      const nome = (state.nomeTransportador ?? "").trim();
      const quem = formatCpfCnpj(documento) + (nome ? " - " + nome : "");
      return consulta
        ? base + ", do CPF/CNPJ " + quem + "."
        : base + ", em nome de " + quem + " (" + papel + ").";
    });

    const avisos = computed(() => {
      const lista = [] as Array<string>;
      switch (state.tipo) {
        case "IMF":
        case "ALT":
          break;
        case "IAR":
          lista.push(
            "O arrendante é o proprietário do veículo (quem consta no CRLV). O contrato é registrado na ANTT com vigência de 3 anos a partir da abertura do pedido; sem o arquivo do contrato, o CRLV é enviado no lugar."
          );
          break;
        case "EXC":
          lista.push(
            "A taxa de R$ 75,00 por placa é cobrada do CPF/CNPJ informado, que pode não ser o seu cliente."
          );
          lista.push(
            "O PIX é pago por quem tem a placa hoje e vence em 24 h; sem pagamento a solicitação é cancelada."
          );
          break;
        case "TRF":
          lista.push(
            "A transferência é uma exclusão na origem e uma inclusão no destino, cada uma com o seu PIX."
          );
          break;
        case "CTR":
        case "CVE":
        case "RPE":
          lista.push("Sem custo na ANTT.");
          break;
        default:
          lista.push(
            "Valores cobrados pela ANTT são pagos pelo transportador."
          );
      }
      if (pago.value && state.tipo !== "TRF") {
        lista.push(
          "Pagamento não realizado em 24 h cancela a solicitação; não há renovação do PIX."
        );
      }
      lista.push(
        "O serviço da GovPass é cobrado conforme a tabela de preços vigente do seu contrato."
      );
      if (state.tipo === "CVE") {
        lista.push(
          "Informe só as placas: não é preciso cadastrar os veículos para consultar."
        );
      } else if (comVeiculos.value) {
        lista.push(
          "Os veículos vêm do cadastro da ANTT. Use Novo veículo para cadastrar placa, renavam, tipo, proprietário e CRLV; se a placa existir na GovPass, os dados vêm preenchidos. A busca por placa também mostra os veículos da GovPass que ainda não estão na ANTT." +
            (state.tipo === "IAR"
              ? " Na inclusão com arrendamento o veículo precisa de CRLV."
              : "")
        );
      }
      lista.push(avisoProcurador.value);
      return lista;
    });

    const precosTexto = computed(() => {
      const precos = tipoInfo.value?.precos ?? [];
      return precos.map((p) => {
        const alvo = p.flServicoVeiculo
          ? " (" + getServicoVeiculoLabel(p.flServicoVeiculo) + ")"
          : "";
        const unidade =
          p.flUnidade === "PLA" ? " por placa" : " por solicitação";
        return (
          (p.valor ? formatMoeda(p.valor) : "Sem custo") +
          (p.valor ? unidade : "") +
          alvo
        );
      });
    });

    const precoVeiculo = (v: VeiculoForm): number | null => {
      const precos = (tipoInfo.value?.precos ?? []).filter(
        (p) => p.flUnidade !== "SOL"
      );
      const exato = precos.find(
        (p) => p.flServicoVeiculo && p.flServicoVeiculo === v.flServicoVeiculo
      );
      const qualquer = precos.find((p) => !p.flServicoVeiculo);
      const preco = exato ?? qualquer;
      return preco && typeof preco.valor === "number" ? preco.valor : null;
    };

    const totalEstimado = computed(() => {
      const precoSolicitacao = (tipoInfo.value?.precos ?? []).find(
        (p) => p.flUnidade === "SOL"
      );
      if (precoSolicitacao) {
        return precoSolicitacao.valor ?? 0;
      }
      return state.veiculos
        .map((v) => precoVeiculo(v) ?? 0)
        .reduce((a, b) => a + b, 0);
    });

    const aoTrocarTipo = () => {
      state.cienteAvisos = false;
      state.erros = [];
    };

    // ---------------------------------------------------------------------
    // Pessoa do pedido (cadastro da ANTT)
    // ---------------------------------------------------------------------

    const ehNovoCadastro = computed(
      () => state.tipo === "CPF" || state.tipo === "CPJ"
    );

    const tituloCnh = computed(() =>
      state.tipo === "CPJ"
        ? "CNH do gestor (sócio proprietário)"
        : "CNH do motorista (o próprio TAC)"
    );

    /**
     * Novo cadastro: o que a ANTT (ou a GovPass) já tem da pessoa vem preenchido; só o que
     * estiver vazio é sobrescrito, para não desfazer o que o usuário digitou.
     */
    const preencherCadastroDaPessoa = (p: PessoaAnttPayload) => {
      const c = state.cadastro;
      const vazio = (v?: string | null) => !v || !v.trim();
      if (vazio(c.rg)) c.rg = p.rg ?? null;
      if (vazio(c.orgaoEmissorIdentidade))
        c.orgaoEmissorIdentidade = p.orgaoEmissorIdentidade ?? null;
      if (vazio(c.ufIdentidade)) c.ufIdentidade = p.ufIdentidade ?? null;
      if (vazio(c.enderecoTipo)) c.enderecoTipo = p.enderecoTipo || "Comercial";
      if (vazio(c.enderecoCep)) c.enderecoCep = p.enderecoCep ?? null;
      if (vazio(c.enderecoLogradouro))
        c.enderecoLogradouro = p.enderecoLogradouro ?? null;
      if (vazio(c.enderecoNumero)) c.enderecoNumero = p.enderecoNumero ?? null;
      if (vazio(c.enderecoBairro)) c.enderecoBairro = p.enderecoBairro ?? null;
      if (vazio(c.contatoTelefone)) c.contatoTelefone = p.telefone ?? null;
      if (vazio(c.contatoEmail)) c.contatoEmail = p.email ?? null;
      if (state.tipo === "CPF") {
        // o motorista é o próprio TAC
        if (vazio(c.cnhCpf)) c.cnhCpf = somenteDigitos(p.cpfCnpj) || null;
        if (vazio(c.cnhNome)) c.cnhNome = p.nome ?? null;
        if (vazio(c.cnhNumero)) c.cnhNumero = p.cnhNumero ?? null;
        if (vazio(c.cnhCategoria)) c.cnhCategoria = p.cnhCategoria ?? null;
        if (vazio(c.cnhSexo)) c.cnhSexo = p.sexo ?? null;
        if (vazio(c.cnhDataNascimento))
          c.cnhDataNascimento = dataDaApi(p.dataNascimento);
      } else if (p.gestor) {
        if (vazio(c.cnhCpf)) c.cnhCpf = formatCpfCnpj(p.gestor.cpfCnpj) || null;
        if (vazio(c.cnhNome)) c.cnhNome = p.gestor.nome ?? null;
        if (vazio(c.cnhDataNascimento))
          c.cnhDataNascimento = dataDaApi(p.gestor.dataNascimento);
      }
    };

    const usarPessoa = (p: PessoaAnttPayload) => {
      state.pessoa = p.idPessoaAntt ? p : null;
      state.pessoaDaGovpass = !p.idPessoaAntt && p.origem === "GOV";
      state.cpfCnpj = formatCpfCnpj(p.cpfCnpj);
      state.documentoIdentificado = somenteDigitos(p.cpfCnpj);
      if (p.nome) {
        state.nomeTransportador = p.nome;
      }
      if (ehNovoCadastro.value) {
        preencherCadastroDaPessoa(p);
      }
    };

    const limparPessoa = () => {
      state.pessoa = null;
      state.pessoaDaGovpass = false;
      state.documentoIdentificado = "";
      state.cpfCnpj = "";
      state.nomeTransportador = "";
    };

    /** CPF/CNPJ informado: a pessoa da ANTT, ou o que a GovPass tem do documento. */
    const identificarPessoa = (): Promise<void> => {
      const documento = somenteDigitos(state.cpfCnpj);
      if (documento === state.documentoIdentificado) {
        return Promise.resolve();
      }
      state.pessoa = null;
      state.pessoaDaGovpass = false;
      state.documentoIdentificado = documento;
      if (!isCpfCnpjValido(documento)) {
        return Promise.resolve();
      }
      const { data, loading, preencherPessoaAntt } = usePessoaAnttService();
      state.loadings.push(loading);
      return preencherPessoaAntt(documento, idClienteBusca.value)
        .then(() => {
          removeLoading(loading);
          const p = data.value as PessoaAnttPayload | null;
          if (p) {
            usarPessoa(p);
          }
        })
        .catch(() => removeLoading(loading));
    };

    /** Lupa: começa pelo que já foi digitado (nome ou parte do documento). */
    const abrirBuscaPessoa = () => {
      state.termoBuscaPessoa =
        (state.nomeTransportador ?? "").trim() || somenteDigitos(state.cpfCnpj);
      state.buscaPessoaAberta = true;
    };

    /** Escolhido na lupa: identifica como se o CPF/CNPJ tivesse sido digitado. */
    const aoEscolherPessoa = (p: PessoaAnttPayload) => {
      state.buscaPessoaAberta = false;
      state.cpfCnpj = formatCpfCnpj(p.cpfCnpj);
      state.nomeTransportador = p.nome ?? "";
      state.documentoIdentificado = "";
      identificarPessoa();
    };

    const abrirCadastroPessoa = (alterar: boolean) => {
      state.dialogPessoa = {
        aberto: true,
        idPessoaAntt: alterar ? state.pessoa?.idPessoaAntt ?? null : null,
        cpfCnpjInicial: somenteDigitos(state.cpfCnpj),
        nomeInicial: state.nomeTransportador ?? ""
      };
    };

    const aoFecharCadastroPessoa = (p?: PessoaAnttPayload) => {
      state.dialogPessoa.aberto = false;
      if (p && p.idPessoaAntt) {
        usarPessoa(p);
      }
    };
    // ---------------------------------------------------------------------
    // Cliente (root) e veículos do cadastro da ANTT
    // ---------------------------------------------------------------------

    const carregarTiposCliente = (idCliente?: number | null) => {
      if (!isRoot.value || !idCliente) {
        state.tiposCliente = null;
        return Promise.resolve();
      }
      return loadTiposCliente(idCliente).then((lista) => {
        state.tiposCliente = lista;
      });
    };

    const confirmSelectionCliente = (c?: ClientePayload | null) => {
      const novo = c && c.idCliente ? c : null;
      if ((novo?.idCliente ?? null) === (state.cliente?.idCliente ?? null)) {
        return;
      }
      state.cliente = novo;
      // pessoa, veículos e busca são do cliente anterior
      limparPessoa();
      state.veiculos = [];
      state.busca.resultados = [];
      carregarTiposCliente(novo?.idCliente);
    };

    /** Linha da tabela a partir do veículo do cadastro da ANTT. */
    const linhaDoVeiculoAntt = (
      v: VeiculoAnttPayload,
      anterior?: VeiculoForm | null
    ): VeiculoForm => ({
      uid: anterior?.uid ?? novoUid(),
      doCadastro: true,
      idVeiculoAntt: v.idVeiculoAntt,
      placa: normalizarPlaca(v.placa),
      renavam: v.renavam,
      chassi: v.chassi,
      flServicoVeiculo: v.flServicoVeiculo,
      possuiCrlv: v.possuiCrlv,
      proprietario: v.proprietario,
      possuiContrato: anterior?.possuiContrato ?? false
    });

    /** Placas do cadastro da ANTT e, com parte da placa, as da GovPass do cliente. */
    const buscarVeiculos = (placa?: string) => {
      if (typeof placa === "string") {
        state.busca.placa = placa;
      }
      const { data, buscarVeiculosParaPreenchimento } = useVeiculoAnttService();
      state.busca.carregando = true;
      buscarVeiculosParaPreenchimento(
        normalizarPlaca(state.busca.placa) || null,
        idClienteBusca.value
      )
        .then(() => {
          state.busca.resultados =
            (data.value as Array<VeiculoAnttPayload>) ?? [];
        })
        .finally(() => {
          state.busca.carregando = false;
        });
    };

    const placaJaAdicionada = (placa?: string | null) =>
      state.veiculos.some(
        (v) => normalizarPlaca(v.placa) === normalizarPlaca(placa)
      );

    /** Consulta de veículos: abre o pop-up já buscando pelo que foi digitado. */
    const abrirBuscaVeiculos = () => {
      state.busca.resultados = [];
      state.busca.aberta = true;
      buscarVeiculos();
    };

    /** Por que a placa não pode ser marcada na consulta (vazio: pode). */
    const motivoBloqueioCadastro = (v: VeiculoAnttPayload) => {
      if (placaJaAdicionada(v.placa)) {
        return "Placa já adicionada";
      }
      if (!v.idVeiculoAntt) {
        return "Da GovPass: cadastre na ANTT (Cadastrar)";
      }
      if (state.tipo === "IAR" && !v.possuiCrlv) {
        return "Sem CRLV: anexe o CRLV no cadastro do veículo (lápis)";
      }
      return "";
    };

    const adicionarDigitado = () => {
      state.veiculos.push({
        uid: novoUid(),
        doCadastro: false,
        placa: normalizarPlaca(state.busca.placa) || "",
        renavam: "",
        chassi: "",
        flServicoVeiculo: null
      });
      state.busca.placa = "";
    };

    /**
     * Consulta de veículo: adiciona as placas digitadas ou coladas (uma por linha, de uma
     * planilha, por exemplo). As inválidas ficam no campo para correção.
     */
    const adicionarPlacas = () => {
      const vistas = state.veiculos.map((v) => normalizarPlaca(v.placa));
      const invalidas = [] as Array<string>;
      const excedentes = [] as Array<string>;
      (state.busca.placa ?? "")
        .split(/[\s,;]+/)
        .filter((p) => p)
        .forEach((digitada) => {
          const placa = normalizarPlaca(digitada);
          if (!isPlacaValida(placa)) {
            invalidas.push(digitada);
          } else if (vistas.indexOf(placa) < 0) {
            if (state.veiculos.length >= MAXIMO_PLACAS_CONSULTA) {
              excedentes.push(placa);
              return;
            }
            vistas.push(placa);
            state.veiculos.push({
              uid: novoUid(),
              doCadastro: false,
              placa,
              renavam: "",
              chassi: "",
              flServicoVeiculo: null
            });
          }
        });
      state.busca.placa = invalidas.concat(excedentes).join(" ");
      if (invalidas.length) {
        useToast().warning(
          "Placa(s) inválida(s), confira no campo: " + invalidas.join(", ")
        );
      }
      if (excedentes.length) {
        useToast().warning(
          "A consulta aceita até " +
            MAXIMO_PLACAS_CONSULTA +
            " placas; as demais ficaram no campo para outra consulta."
        );
      }
    };

    /**
     * Placa e renavam só se digitam na linha da placa avulsa (exclusão, consulta). Do cadastro ou
     * da consulta vêm prontos; na inclusão, quem corrige é o cadastro do veículo (botão +).
     */
    const somenteLeitura = (v: VeiculoForm) =>
      v.doCadastro || !!v.daConsulta || exigeServicoVeiculo.value;

    const removerVeiculo = (index: number) => {
      state.veiculos.splice(index, 1);
    };

    const idArquivoContrato = (v: VeiculoForm) => "rntrc-contrato-" + v.uid;

    const escolherContrato = (v: VeiculoForm) => {
      document.getElementById(idArquivoContrato(v))?.click();
    };

    const aoEscolherContrato = (v: VeiculoForm, e: Event) => {
      const campo = e.target as HTMLInputElement;
      v.nomeContrato =
        campo.files && campo.files.length ? campo.files[0].name : null;
    };

    const abrirCadastroVeiculo = (
      idVeiculoAntt: number | null,
      placa: string | null,
      indice: number
    ) => {
      const linha = indice >= 0 ? state.veiculos[indice] : null;
      const semCadastro = !!linha && !linha.doCadastro;
      state.dialogVeiculo = {
        aberto: true,
        idVeiculoAntt,
        placaInicial: normalizarPlaca(placa),
        renavamInicial: semCadastro ? somenteDigitos(linha?.renavam) : null,
        flServicoVeiculoInicial: semCadastro ? linha?.flServicoVeiculo : null,
        indice
      };
    };

    /**
     * IMF: placa de outro proprietário não entra. O aviso oferece atualizar o cadastro do veículo
     * (o CRLV pode ter mudado de dono); salvo, ele volta pela mesma conferência.
     */
    const avisarProprietarioDiferente = (
      v: VeiculoAnttPayload,
      dono: PessoaAnttPayload,
      indice: number
    ) => {
      useConfirm().require({
        header: "Proprietário diferente",
        icon: "pi pi-exclamation-triangle",
        message:
          "A placa " +
          formatPlaca(v.placa) +
          " está no cadastro como de " +
          getNomePessoaAntt(v.proprietario) +
          ". Na inclusão própria todas as placas precisam ser do mesmo proprietário, que é o transportador do pedido: " +
          getNomePessoaAntt(dono) +
          ". Por isso esta placa não pode entrar. Se o veículo mudou de dono, atualize o cadastro dele.",
        acceptLabel: "Atualizar cadastro do veículo",
        rejectLabel: "Cancelar",
        accept: () =>
          abrirCadastroVeiculo(v.idVeiculoAntt ?? null, v.placa ?? null, indice)
      });
    };

    /**
     * Placas marcadas na consulta: entram as que podem. IMF: as de outro proprietário ficam de
     * fora, e a primeira abre o aviso com a opção de atualizar o cadastro do veículo.
     */
    const aoConfirmarVeiculos = (lista: Array<VeiculoAnttPayload>) => {
      state.busca.aberta = false;
      const recusadas = [] as Array<VeiculoAnttPayload>;
      lista.forEach((v) => {
        if (motivoBloqueioCadastro(v)) {
          return;
        }
        if (proprietarioDivergente(v)) {
          recusadas.push(v);
          return;
        }
        state.veiculos.push(linhaDoVeiculoAntt(v));
      });
      state.busca.placa = "";
      const dono = recusadas.length
        ? proprietarioDivergente(recusadas[0])
        : null;
      if (dono) {
        if (recusadas.length > 1) {
          useToast().warning(
            "Não entraram, por serem de outro proprietário: " +
              recusadas.map((r) => formatPlaca(r.placa)).join(", ") +
              "."
          );
        }
        avisarProprietarioDiferente(recusadas[0], dono, -1);
      }
    };

    /** Veículo salvo no cadastro: entra (ou é atualizado) na tabela e na busca. */
    const aoFecharCadastroVeiculo = (v?: VeiculoAnttPayload) => {
      const indice = state.dialogVeiculo.indice;
      state.dialogVeiculo.aberto = false;
      if (!v || !v.idVeiculoAntt) {
        return;
      }
      // A linha da GovPass (sem id) vira a do cadastro da ANTT recém-criado.
      const naBusca = state.busca.resultados.findIndex(
        (r) =>
          r.idVeiculoAntt === v.idVeiculoAntt ||
          (!r.idVeiculoAntt &&
            normalizarPlaca(r.placa) === normalizarPlaca(v.placa))
      );
      if (naBusca >= 0) {
        state.busca.resultados.splice(naBusca, 1, v);
      }
      const naTabela =
        indice >= 0
          ? indice
          : state.veiculos.findIndex(
              (r) =>
                r.idVeiculoAntt === v.idVeiculoAntt ||
                normalizarPlaca(r.placa) === normalizarPlaca(v.placa)
            );
      // IMF: depois do cadastro, a placa só fica (ou entra) se for do mesmo proprietário.
      const dono = proprietarioDivergente(v, naTabela);
      if (dono) {
        if (naTabela >= 0) {
          state.veiculos.splice(naTabela, 1);
        }
        avisarProprietarioDiferente(v, dono, -1);
        return;
      }
      if (naTabela >= 0) {
        state.veiculos.splice(
          naTabela,
          1,
          linhaDoVeiculoAntt(v, state.veiculos[naTabela])
        );
      } else if (comVeiculos.value) {
        state.veiculos.push(linhaDoVeiculoAntt(v));
      }
    };

    // ---------------------------------------------------------------------
    // Validação e envio
    // ---------------------------------------------------------------------

    const validarNovoCadastro = (erros: Array<string>) => {
      const c = state.cadastro;
      const obrigatorios: Array<{
        valor?: string | null;
        nome: string;
      }> = [
        { valor: c.enderecoTipo, nome: "tipo de endereço" },
        { valor: c.enderecoCep, nome: "CEP" },
        { valor: c.enderecoLogradouro, nome: "logradouro" },
        { valor: c.enderecoNumero, nome: "número" },
        { valor: c.enderecoBairro, nome: "bairro" },
        { valor: c.contatoTelefone, nome: "telefone" },
        { valor: c.contatoEmail, nome: "e-mail" }
      ];
      if (state.tipo === "CPF") {
        obrigatorios.push({ valor: c.rg, nome: "RG" });
        obrigatorios.push({
          valor: c.orgaoEmissorIdentidade,
          nome: "órgão emissor"
        });
        obrigatorios.push({ valor: c.ufIdentidade, nome: "UF da identidade" });
        obrigatorios.push({ valor: c.cnhCategoria, nome: "categoria da CNH" });
        obrigatorios.push({ valor: c.cnhSexo, nome: "sexo do motorista" });
      } else {
        obrigatorios.push({ valor: c.cnhNome, nome: "nome do gestor" });
      }
      obrigatorios
        .filter((o) => !o.valor)
        .forEach((o) => erros.push("Informe o " + o.nome + "."));
      if (state.tipo === "CPJ" && !isCpfCnpjValido(c.cnhCpf)) {
        erros.push("Informe um CPF válido para o gestor.");
      }
      if (!dataParaApi(c.cnhDataNascimento)) {
        erros.push("Informe a data de nascimento da CNH (dd/mm/aaaa).");
      }
    };

    const arquivoContrato = (v: VeiculoForm): File | null => {
      const campo = document.getElementById(
        idArquivoContrato(v)
      ) as HTMLInputElement | null;
      return campo && campo.files && campo.files.length ? campo.files[0] : null;
    };

    const validarVeiculos = (erros: Array<string>) => {
      if (!state.veiculos.length) {
        erros.push("Adicione ao menos uma placa.");
      }
      const vistas = [] as Array<string>;
      state.veiculos.forEach((v, i) => {
        const placa = normalizarPlaca(v.placa);
        const ref = "Linha " + (i + 1) + (placa ? " (" + placa + ")" : "");
        if (!isPlacaValida(placa)) {
          erros.push(
            ref + ": placa inválida (7 caracteres, Mercosul ou antiga)."
          );
        } else if (vistas.indexOf(placa) >= 0) {
          erros.push(ref + ": placa repetida.");
        }
        vistas.push(placa);
        if (ehInclusao.value) {
          if (!v.idVeiculoAntt) {
            erros.push(ref + ": cadastre o veículo (botão ao lado da placa).");
            return;
          }
          if (!isRenavamValido(v.renavam)) {
            erros.push(
              ref + ": renavam do cadastro inválido; altere o veículo."
            );
          }
        } else if (somenteDigitos(v.renavam) && !isRenavamValido(v.renavam)) {
          erros.push(ref + ": renavam deve ter 11 dígitos.");
        }
        if (state.tipo === "IAR") {
          if (!v.possuiCrlv) {
            erros.push(
              ref + ": veículo sem CRLV; anexe no cadastro do veículo."
            );
          }
          if (
            v.proprietario?.cpfCnpj &&
            somenteDigitos(v.proprietario.cpfCnpj) ===
              somenteDigitos(state.cpfCnpj)
          ) {
            erros.push(
              ref +
                ": o proprietário é o próprio arrendatário; para veículo próprio use a inclusão própria."
            );
          }
        }
      });
      if (state.tipo === "IMF") {
        const donos = state.veiculos
          .filter((v) => v.proprietario?.cpfCnpj)
          .map((v) => somenteDigitos(v.proprietario?.cpfCnpj))
          .filter((d, i, lista) => lista.indexOf(d) === i);
        if (donos.length > 1) {
          erros.push(
            "Na inclusão própria todos os veículos precisam ser do mesmo proprietário: ele é o transportador do pedido."
          );
        }
      }
    };

    const validar = (): Array<string> => {
      const erros = [] as Array<string>;
      if (isRoot.value && !state.cliente?.idCliente) {
        erros.push("Escolha o cliente da solicitação.");
      }
      if (!state.tipo) {
        erros.push("Escolha o serviço.");
        return erros;
      }
      if (exigeCpfCnpj.value && state.tipo !== "IMF") {
        if (!somenteDigitos(state.cpfCnpj)) {
          erros.push("Informe o CPF/CNPJ.");
        } else if (!isCpfCnpjValido(state.cpfCnpj)) {
          erros.push("CPF/CNPJ inválido.");
        } else if (
          exigeNomePessoa.value &&
          !(state.nomeTransportador ?? "").trim()
        ) {
          erros.push(
            "Informe o nome do " +
              papelPessoa.value.toLowerCase() +
              " ou use o botão Novo " +
              papelPessoa.value.toLowerCase() +
              "."
          );
        }
      }
      if (state.tipo === "TRF" && !isCpfCnpjValido(state.cpfCnpjDestino)) {
        erros.push("CPF/CNPJ de destino inválido.");
      }
      if (ehNovoCadastro.value) {
        validarNovoCadastro(erros);
      }
      if (comVeiculos.value) {
        validarVeiculos(erros);
      }
      if (!state.cienteAvisos) {
        erros.push('Marque "Li e concordo" nos avisos.');
      }
      return erros;
    };

    const montarPayload = (
      contratos: Record<string, string>
    ): RntrcSolicitacaoPayload => {
      const comPessoa = exigeCpfCnpj.value && state.tipo !== "IMF";
      const payload: RntrcSolicitacaoPayload = {
        cliente:
          isRoot.value && state.cliente?.idCliente
            ? ({ idCliente: state.cliente.idCliente } as ClientePayload)
            : null,
        flTipoServico: state.tipo,
        idPessoaAntt: comPessoa ? state.pessoa?.idPessoaAntt ?? null : null,
        cpfCnpj: comPessoa ? somenteDigitos(state.cpfCnpj) : null,
        nomeTransportador: comPessoa
          ? state.nomeTransportador?.trim() || null
          : null,
        cpfCnpjDestino:
          state.tipo === "TRF" ? somenteDigitos(state.cpfCnpjDestino) : null,
        flProsseguirParcial: state.flProsseguirParcial,
        cienteAvisos: true,
        observacao: state.observacao?.trim() || null,
        veiculos: comVeiculos.value
          ? state.veiculos.map((v, i) => ({
              idVeiculoAntt: v.idVeiculoAntt ?? null,
              ordem: i + 1,
              placa: normalizarPlaca(v.placa),
              renavam: somenteDigitos(v.renavam) || null,
              chassi: (v.chassi ?? "").trim() || null,
              flServicoVeiculo: v.flServicoVeiculo ?? null,
              possuiContrato: state.tipo === "IAR" ? !!v.possuiContrato : null,
              keyBlContratoTemp:
                state.tipo === "IAR" ? contratos[v.uid] ?? null : null
            }))
          : []
      };
      if (ehNovoCadastro.value) {
        Object.assign(payload, state.cadastro, {
          enderecoCep: somenteDigitos(state.cadastro.enderecoCep) || null,
          cnhCpf: somenteDigitos(state.cadastro.cnhCpf) || null,
          cnhDataNascimento: dataParaApi(state.cadastro.cnhDataNascimento)
        });
      }
      return payload;
    };

    /** IAR: sobe o contrato de cada placa para o storage temporário antes de enviar. */
    const enviarContratos = async (): Promise<Record<string, string>> => {
      const contratos: Record<string, string> = {};
      if (state.tipo !== "IAR") {
        return contratos;
      }
      for (const v of state.veiculos) {
        const arquivo = arquivoContrato(v);
        if (!arquivo) {
          continue;
        }
        const formData = new FormData();
        formData.append("file", arquivo);
        const storage = useStorage();
        state.loadings.push(storage.loading);
        try {
          contratos[v.uid] = await storage.uploadTempFile(formData);
        } finally {
          removeLoading(storage.loading);
        }
      }
      return contratos;
    };

    const descartarContratos = (contratos: Record<string, string>) => {
      Object.values(contratos).forEach((key) =>
        useStorage().deleteTempFile(key)
      );
    };

    const salvar = async () => {
      state.erros = validar();
      if (state.erros.length) {
        useToast().warning("Confira os campos destacados antes de enviar.");
        return;
      }
      let contratos: Record<string, string> = {};
      try {
        contratos = await enviarContratos();
      } catch (e) {
        return;
      }
      const service = useRntrcSolicitacaoService();
      state.loadings.push(service.loading);
      const payload = montarPayload(contratos);
      (props.idRntrcSolicitacao
        ? service.atualizar(props.idRntrcSolicitacao, payload)
        : service.criar(payload)
      )
        .then(() => {
          removeLoading(service.loading);
          const salvo = service.data.value as RntrcSolicitacaoPayload | null;
          const id = salvo?.idRntrcSolicitacao ?? props.idRntrcSolicitacao;
          useToast().success(
            "Solicitação" +
              (id ? " nº " + id : "") +
              " enviada. O robô assume em seguida; acompanhe a situação na lista."
          );
          if (id) {
            router.push({
              name: "rntrc-solicitacao-detalhe",
              params: { idRntrcSolicitacao: id + "" }
            });
          } else {
            router.push({ name: "rntrc-solicitacao" });
          }
        })
        .catch(() => {
          removeLoading(service.loading);
          descartarContratos(contratos);
        });
    };

    const voltar = () => {
      router.push({ name: "rntrc-solicitacao" });
    };

    // ---------------------------------------------------------------------
    // Carga inicial (edição, "Incluir no destino", "Fazer cadastro")
    // ---------------------------------------------------------------------

    const veiculoDoDto = (
      v: RntrcSolicitacaoVeiculoPayload,
      s: RntrcSolicitacaoPayload,
      manterCadastro: boolean
    ): VeiculoForm => {
      const doCadastro = manterCadastro && !!v.idVeiculoAntt;
      let proprietario: PessoaAnttPayload | null = null;
      if (doCadastro && s.flTipoServico === "IAR") {
        proprietario = { cpfCnpj: v.cpfCnpjArrendante, nome: v.nomeArrendante };
      } else if (doCadastro && s.flTipoServico === "IMF") {
        proprietario = { cpfCnpj: s.cpfCnpj, nome: s.nomeTransportador };
      }
      return {
        uid: novoUid(),
        doCadastro,
        idVeiculoAntt: doCadastro ? v.idVeiculoAntt : null,
        placa: normalizarPlaca(v.placa),
        renavam: v.renavam ?? "",
        chassi: v.chassi ?? "",
        flServicoVeiculo: v.flServicoVeiculo ?? null,
        possuiCrlv: v.possuiCrlv,
        possuiContrato: manterCadastro ? !!v.possuiContrato : false,
        proprietario
      };
    };

    /** Placas sem cadastro (ex.: vindas de uma exclusão): liga as que já estão na ANTT. */
    const ligarPlacasAoCadastro = () => {
      state.veiculos
        .filter((v) => !v.idVeiculoAntt && isPlacaValida(v.placa))
        .forEach((v) => {
          const { data, preencherVeiculoAntt } = useVeiculoAnttService();
          preencherVeiculoAntt(
            normalizarPlaca(v.placa),
            idClienteBusca.value
          ).then(() => {
            const encontrado = data.value as VeiculoAnttPayload | null;
            const idx = state.veiculos.indexOf(v);
            if (encontrado?.idVeiculoAntt && idx >= 0) {
              state.veiculos.splice(idx, 1, linhaDoVeiculoAntt(encontrado, v));
            }
          });
        });
    };

    const carregarEdicao = (id: number) => {
      const { data, loading, buscar } = useRntrcSolicitacaoService();
      state.loadings.push(loading);
      return buscar(id)
        .then(() => {
          removeLoading(loading);
          const s = data.value as RntrcSolicitacaoPayload;
          if (!s) {
            return;
          }
          state.tipo = s.flTipoServico ?? null;
          if (isRoot.value && s.cliente?.idCliente) {
            state.cliente = s.cliente;
            carregarTiposCliente(s.cliente.idCliente);
          }
          state.pessoa = s.idPessoaAntt
            ? {
                idPessoaAntt: s.idPessoaAntt,
                cpfCnpj: s.cpfCnpj,
                nome: s.nomeTransportador
              }
            : null;
          state.cpfCnpj = formatCpfCnpj(s.cpfCnpj);
          state.documentoIdentificado = somenteDigitos(s.cpfCnpj);
          state.nomeTransportador = s.nomeTransportador ?? "";
          state.cpfCnpjDestino = formatCpfCnpj(s.cpfCnpjDestino);
          state.flProsseguirParcial = !!s.flProsseguirParcial;
          state.observacao = s.observacao ?? "";
          state.veiculos = (s.veiculos ?? []).map((v) =>
            veiculoDoDto(v, s, true)
          );
          state.cadastro = {
            rg: s.rg,
            orgaoEmissorIdentidade: s.orgaoEmissorIdentidade,
            ufIdentidade: s.ufIdentidade,
            enderecoTipo: s.enderecoTipo,
            enderecoCep: s.enderecoCep,
            enderecoLogradouro: s.enderecoLogradouro,
            enderecoNumero: s.enderecoNumero,
            enderecoBairro: s.enderecoBairro,
            contatoTelefone: s.contatoTelefone,
            contatoEmail: s.contatoEmail,
            cnhCpf: s.cnhCpf,
            cnhNome: s.cnhNome,
            cnhDataNascimento: dataDaApi(s.cnhDataNascimento),
            cnhNumero: s.cnhNumero,
            cnhCategoria: s.cnhCategoria,
            cnhSexo: s.cnhSexo
          };
          if (
            s.flSituacao &&
            s.flSituacao !== "AGR" &&
            s.flSituacao !== "ERR"
          ) {
            useToast().warning(
              "Esta solicitação não pode mais ser editada (só em Aguardando robô ou Erro)."
            );
          }
        })
        .catch(() => removeLoading(loading));
    };

    /** Exclusão, transferência e alteração: no nome de quem tem a placa hoje. */
    const noNomeDoDonoAtual = computed(() =>
      servicoNoNomeDoDonoAtual(state.tipo)
    );

    /**
     * Consulta de veículo → outro serviço: as placas escolhidas, com renavam e tipo do extrato.
     * Nada é salvo aqui: na inclusão o cliente ainda cadastra cada veículo com o CRLV.
     */
    const aplicarConsulta = (s: RntrcSolicitacaoPayload) => {
      const ids = props.idsVeiculosOrigem ?? [];
      const escolhidos = (s.veiculos ?? []).filter(
        (v) =>
          !ids.length ||
          (!!v.idRntrcSolicitacaoVeiculo &&
            ids.indexOf(v.idRntrcSolicitacaoVeiculo) >= 0)
      );
      state.veiculos = escolhidos.map((v) => ({
        uid: novoUid(),
        doCadastro: false,
        daConsulta: true,
        placa: normalizarPlaca(v.placa),
        renavam: v.renavamAtual ?? "",
        chassi: "",
        flServicoVeiculo: v.flServicoVeiculoAtual ?? null
      }));
      const dono = escolhidos.find((v) => !!v.cpfCnpjAtual);
      if (noNomeDoDonoAtual.value && dono) {
        state.cpfCnpj = formatCpfCnpj(dono.cpfCnpjAtual);
        state.nomeTransportador = dono.nomeTransportadorAtual ?? "";
        identificarPessoa();
      }
      ligarPlacasAoCadastro();
    };

    const carregarOrigem = (idOrigem: number) => {
      const { data, loading, buscar } = useRntrcSolicitacaoService();
      state.loadings.push(loading);
      return buscar(idOrigem)
        .then(() => {
          removeLoading(loading);
          const s = data.value as RntrcSolicitacaoPayload;
          if (!s) {
            return;
          }
          state.origem = s;
          if (isRoot.value && s.cliente?.idCliente) {
            // a inclusão no destino (e o cadastro) ficam no mesmo cliente da origem
            state.cliente = s.cliente;
            carregarTiposCliente(s.cliente.idCliente);
          }
          if (s.flTipoServico === "CVE") {
            return aplicarConsulta(s);
          }
          if (ehNovoCadastro.value) {
            // consulta "sem cadastro": o mesmo documento, com o que a ANTT ou a GovPass têm dele
            state.cpfCnpj = formatCpfCnpj(s.cpfCnpj);
            state.nomeTransportador = s.nomeTransportador ?? "";
            return identificarPessoa();
          }
          state.veiculos = (s.veiculos ?? [])
            .filter((v) => v.flIncluido !== false)
            .map((v) => veiculoDoDto(v, s, false));
          ligarPlacasAoCadastro();
        })
        .catch(() => removeLoading(loading));
    };

    onMounted(async () => {
      await loadTipos();
      if (props.idRntrcSolicitacao) {
        await carregarEdicao(props.idRntrcSolicitacao);
      } else {
        const tipoInicial = props.tipoInicial as TipoServicoRntrc | undefined;
        if (tipoInicial && TIPOS_FORMULARIO.indexOf(tipoInicial) >= 0) {
          state.tipo = tipoInicial;
        } else if (tiposOpcoes.value.length === 1) {
          state.tipo = tiposOpcoes.value[0].flag;
        }
        if (props.idOrigem) {
          await carregarOrigem(props.idOrigem);
        }
      }
      state.stateReady = true;
    });

    return {
      stateLoading,
      isRoot,
      clienteOk,
      confirmSelectionCliente,
      tiposOpcoes,
      titulo,
      comVeiculos,
      pago,
      exigeCpfCnpj,
      exigeNomePessoa,
      exigeServicoVeiculo,
      permiteVeiculoDigitado,
      papelPessoa,
      labelTransportador,
      tituloProprietario,
      cpfCnpjInvalido,
      situacaoPessoa,
      transportadorDosVeiculos,
      proprietariosDivergentes,
      proprietarioDivergente,
      avisos,
      precosTexto,
      precoVeiculo,
      totalEstimado,
      aoTrocarTipo,
      ehNovoCadastro,
      noNomeDoDonoAtual,
      tituloCnh,
      categoriasCnh: CATEGORIAS_CNH,
      sexosCnh: SEXOS_CNH,
      identificarPessoa,
      abrirBuscaPessoa,
      aoEscolherPessoa,
      limparPessoa,
      abrirCadastroPessoa,
      aoFecharCadastroPessoa,
      buscarVeiculos,
      abrirBuscaVeiculos,
      aoConfirmarVeiculos,
      motivoBloqueioCadastro,
      placaJaAdicionada,
      adicionarDigitado,
      adicionarPlacas,
      somenteLeitura,
      removerVeiculo,
      idArquivoContrato,
      escolherContrato,
      aoEscolherContrato,
      abrirCadastroVeiculo,
      aoFecharCadastroVeiculo,
      salvar,
      voltar,
      formatMoeda,
      formatPlaca,
      getServicoVeiculoLabel,
      getNomePessoaAntt,
      isCpfCnpjValido,
      isPlacaValida,
      isRenavamValido,
      normalizarPlaca,
      servicosVeiculo: SERVICOS_VEICULO_RNTRC,
      ...toRefs(state)
    };
  },
  components: {
    Checkbox,
    EntitySelectorCliente,
    BuscaPessoaAntt,
    BuscaVeiculoAntt,
    CadastroPessoaAntt,
    CadastroVeiculoAntt
  },
  props: {
    idRntrcSolicitacao: Number,
    tipoInicial: String,
    idOrigem: Number,
    /** Consulta de veículo → outro serviço: as placas escolhidas (vazio: todas). */
    idsVeiculosOrigem: Array as PropType<Array<number>>
  }
});
</script>

<style scoped lang="scss">
// Placa e renavam digitados na linha: o campo ocupa a coluna, sem invadir a vizinha.
.rntrc-input-celula {
  width: 100%;
  min-width: 0;
}

.rntrc-rotulo {
  color: $text-muted;
}

.rntrc-precos {
  font-size: $font-size-sm;
}

.rntrc-contrato {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $space-1;
}

.rntrc-arquivo-oculto {
  display: none;
}

// O .p-button global tem min-width de 10rem; aqui o botão ocupa só o próprio texto.
.rntrc-contrato .p-button.rntrc-botao-arquivo {
  min-width: auto;
  width: auto;
}

.rntrc-contrato-dica {
  color: $text-secondary;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rntrc-aviso {
  padding: $space-3 $space-4;
  border-radius: $radius-md;
  border: 1px solid $border-color-light;
  font-size: $font-size-sm;

  &--info {
    background: $info-light;
    border-left: 4px solid $info;
  }

  &--warning {
    background: $warning-bg;
    border-left: 4px solid $warning;
  }

  &--danger {
    background: $danger-light;
    border-left: 4px solid $danger;
    color: $danger;
  }

  &--muted {
    background: $bg-surface-secondary;
    border-left: 4px solid $text-muted;
  }
}
</style>
